import React, { useState, useEffect, useImperativeHandle, useCallback, useMemo, useRef } from 'react';
import { iDataTableProps, iQueryFilters } from './components/iTableProps';

import DataTableInner from './components/DataTable';
import CardTable from './components/CardTable';
import { TableContext } from './utils/context';
import { getColumns } from './utils/tools';
import SwitchButton from './components/SwitchButton';
import omit from 'lodash/omit';
import { useDeepCompareEffect } from './hooks/useDeepCompareEffect';
import { useDebounceFn } from './hooks/useDebounceFn';
import './Table.less';

const defaultProps = {
  fillSpace: true, // 默认表格填充（合计行、分页置底）
  pagination: true,
  showRowNum: false,
  autoQuery: true, // 是否自动请求
  operationItems: [], // 操作列按钮节点
  operationType: 'button', // 操作列类型
  rowKey: 'id',
  isBigData: false,
  isSingleFind: true,
  isSum: false,
  isDragColumn: true,
  isFilterColumn: true,
  isSort: true,
  isSingleFilter: true,
  sort: { mode: 'single', backSource: false },
  gutter: [16, 16],
  columnCount: 4,
  itemType: "horizontal",
  showModeSwitch: true,
  mode: "table",
  defaultMode: "table",
  operationTypeProps: {
    maxCount: 3,
    hoverVisible: false
  },
  bodyDisplayInRow: true
};

/**
 * todo
 * 1. 请求参数处理钩子要不要给，不给也可以在自定义request里面直接处理
 * 2. 外部params变化时，自动触发请求处理。
 */

const DataTable = React.memo(React.forwardRef(function DataTable (props:iDataTableProps, ref:any) {
  const {
    mode = "table",
    data = [],
    children,
    isDragColumn,
    isSort,
    sort = { mode: 'single', backSource: false },
    defaultMode,
    fillSpace,
    showModeSwitch,
    toolBarClassName,
    renderToolBar,
    searchContent,
    rowSelection,
    autoQuery,
    postData,
    onRequestSuccess,
    onRequestError,
    request,
    params,
    rowKey
  } = props;

  const defaultPageSize = props?.pagination?.pageSize || props?.pagination?.defaultPageSize || 50;
  const defaultPageConfig = {
    pageSizeOptions: ["10", "20", "30", "50", "80", "100", "200", "500", "1000"],
    showSizeChanger: true,
    current: 1,
    pageSize: defaultPageSize,
    total: 0
  }
  const defaultShowRowNum = { base: 1, width: 50 };
  const [dataSource, setDataSource] = useState(data || []);
  const [pageInfo, setPageInfo] = useState(defaultPageConfig);
  // const [sortInfo, setSortInfo] = useState([]);
  const [selectedRowkeys, setSelectedRowkeys] = useState([]);
  const [showRowNum, setShowRowNum] = useState(props.showRowNum);
  const [columnsInner, setColumnsInner] = useState([]);

  const [innerMode, setInnerMode] = useState(mode || defaultMode);
  const queryFiltersRef = useRef({ params, page: defaultPageConfig }); // 过滤条件临时存储
  const selectedRowData = useRef([]);
  const preserveDataSourceMap = useRef({});// 存储每页数据信息map
  const fetchDataOnce = useRef({
    once: false,
    data: []
  });// 是否一次性请求，然后进行前端分页

  /**
   * 有参数request时，从接口请求数据用。
   */
  const fetchData = useCallback(async () => {
    if (request && typeof request === 'function') {
      try {
        const res = await request(queryFiltersRef.current);
        const { data, total, success } = res || {};
        if (success) {
          let dataList = data;
          if (postData && typeof (postData) === "function") {
            dataList = postData(data);
          }
          if (typeof total === "undefined") {
            fetchDataOnce.current = {
              once: true,
              data: dataList
            };
          }

          setPageConfig({ ...(queryFiltersRef.current?.page || {}), total: fetchDataOnce.current.once === true ? dataList.length : total });
          setDataSource(fetchDataOnce.current.once === true ? dataList?.slice(queryFiltersRef.current?.page.current * queryFiltersRef.current?.page.pageSize - queryFiltersRef.current?.page.pageSize, queryFiltersRef.current?.page.current * queryFiltersRef.current?.page.pageSize) : (dataList || []));
          onRequestSuccess && onRequestSuccess(dataList);
        } else {
          onRequestError && onRequestError(res)
        }
      } catch (e) {
        // if (onRequestError === undefined) throw new Error(e as string);
        if (dataSource === undefined) setDataSource([]);

        onRequestError && onRequestError(e)
      }
    }
  }, [request, queryFiltersRef.current])

  const fetchList = useDebounceFn(fetchData, 40);

  useDeepCompareEffect(() => {
    setColumnsInner(getColumns(props.columns, children, { isSort, sortInfo: queryFiltersRef.current?.sort, sort }))
  }, [props.columns, props.children, queryFiltersRef.current?.sort])

  // 数据源变化时，更新preserveDataSourceMap
  useMemo(() => {
    dataSource.forEach((item, index) => {
      preserveDataSourceMap.current[item[rowKey]] = { ...item };
    })
  }, [dataSource])

  // // 被选中项发生变化时，计算生成被选中行数据。
  selectedRowData.current = useMemo(() => {
    return selectedRowkeys.map(key => preserveDataSourceMap.current[key]);
  }, [selectedRowkeys])

  useImperativeHandle(ref, () => {
    return {
      reload: () => {
        setPageConfig({ ...pageInfo, current: 1 })
        const { page } = queryFiltersRef.current;
        fetchDataOnce.current = {}
        setShowRowNumBase({ base: page.current ? (page.current - 1) * page.pageSize + 1 : 1 })
        getData()
      },
      /**
       * 和queryParams结构一样
       * @param paramsAll
       * {
       *  page:{current:1,pageSize:20},
       *  sort:[{field:xx, order:'',orderNum:1}],
       *  params:any
       * }
       */
      queryData: (paramsAll?:iQueryFilters) => {
        const { sort, page, params } = paramsAll || {};
        try {
          if (params) {
            queryFiltersRef.current = { ...queryFiltersRef.current, params }
          }
          if (page) {
            setPageConfig({ ...paramsAll.page })

            const { page } = queryFiltersRef.current;
            setShowRowNumBase({ base: page.current ? (page.current - 1) * page.pageSize + 1 : 1 })
          }
          if (sort) {
            queryFiltersRef.current = { ...queryFiltersRef.current, sort }
          }
          fetchDataOnce.current = {}
          getData()
        } catch (e) {
          console.log(e, "queryData")
        }
      },
      resetAll: () => {
        setPageInfo({ ...defaultPageConfig, current: 1 })
        // setSortInfo([])
        queryFiltersRef.current = { params };
        setSelectedRowkeys([])
      },
      getSelectedRowKeys: () => {
        return selectedRowkeys;
      },
      getSelectedRowData: () => {
        return selectedRowData.current;
      },
      setSeletedRowkeys: (rowKeys) => {
        setSelectedRowkeys(rowKeys);
      },
      clearSelectedRowKeys: () => {
        setSelectedRowkeys([]);
      },
      getQueryFilters: () => {
        return queryFiltersRef.current
      },
      getDataSource: () => {
        return dataSource
      }

    }
  })

  useEffect(() => {
    setInnerMode(mode)
  }, [mode])

  // 分页信息
  useEffect(() => {
    setPageConfig()
  }, [props?.pagination, props?.data])

  useEffect(() => {
    if (rowSelection) {
      const { selectedRowkeys } = rowSelection;
      selectedRowkeys && setSelectedRowkeys(selectedRowkeys);
    }
  }, [rowSelection])
  // 行编号处理
  useEffect(() => {
    setShowRowNumBase();
  }, [props.showRowNum])

  useDeepCompareEffect(() => {
    if (isSort) {
      const { backSource } = props.sort || {};
      backSource && fetchList.run()
    }
  }, [queryFiltersRef.current?.sort])

  /**
   * 设置分页信息
   * @param pageConfig 内部事件触发的分页设置
   */
  function setPageConfig (pageConfig = {}) {
    let filters = {};
    // 如果分页是boolean，并且true，默认内部分页处理。
    if (typeof props.pagination === 'boolean' && props?.pagination) {
      filters = {
        ...defaultPageConfig,
        ...(pageInfo || {}),
        total: props?.data?.length || 0, // 自定义数据源显示分页，需要data总数作为total
        ...pageConfig

      }
      setPageInfo(filters)
    } else if (typeof props.pagination !== 'boolean' && props?.pagination) {
      // 如果自定义分页信息，需要分页合并处理
      filters = {
        ...defaultPageConfig,
        ...(pageInfo || {}),
        ...props.pagination,
        total: props.pagination.total || props?.data?.length || 0, // 外部传入total > 数据总数
        ...pageConfig // 内部事件触发的分页变化

      }
      setPageInfo(filters)
    } else {
      filters = false
      // 不分页
      setPageInfo(false)
    }
    queryFiltersRef.current = {
      ...queryFiltersRef.current,
      page: filters
    }
  }

  /**
   * @description: 设置行编号信息
  * @param {*} rowNumOptions
  * @return {*} void
   */
  function setShowRowNumBase (rowNumOptions = {}) {
    if (typeof props.showRowNum === "object") {
      setShowRowNum({ ...props.showRowNum, ...props.showRowNum, ...rowNumOptions });
    } else if (!props.showRowNum) {
      setShowRowNum(false);
    } else {
      setShowRowNum({ ...defaultShowRowNum, ...rowNumOptions });
    }
  }
  // 分页变化触发数据处理
  function onChange (params) {
    console.log(params, "onChange")
    const { current, pageSize } = params;
    setShowRowNumBase({ base: current ? (current - 1) * pageSize + 1 : 1 })
    setPageConfig(params)
    getData();
    props.onChange && props.onChange(params);
  }

  async function getData (isQuery = true) {
    // 如果是外部控制数据源
    if (props.data) {
      if (queryFiltersRef.current.page) {
        const { page } = queryFiltersRef.current;
        const { current, pageSize } = page;
        setDataSource(props?.pagination?.total ? props.data : props.data.slice(current * pageSize - pageSize, current * pageSize));
      } else {
        setDataSource(props.data);
      }
    }
    if (fetchDataOnce.current.once) {
      const { data } = fetchDataOnce.current
      if (pageInfo) {
        const { page } = queryFiltersRef.current;
        const { current, pageSize } = page;
        setDataSource(data.slice(current * pageSize - pageSize, current * pageSize));
      } else {
        setDataSource(data);
      }
    } else if (request) {
      isQuery && fetchData();
    }
  }

  useEffect(() => {
    getData(autoQuery);
  }, [props.data])

  const restProps = omit(props, ["request", "data", "pagination", "columns"]);

  const TableProComp = useMemo(() => {
    const map = {
      table: DataTableInner,
      card: CardTable
    }
    return map[innerMode];
  }, [innerMode])

  function changeMode (mode) {
    setInnerMode(mode);
  }

  const toolBarContent = useMemo(() => {
    return renderToolBar && typeof renderToolBar === "function" ? renderToolBar() : null
  }, [renderToolBar])

  const sortConfig = useMemo(() => {
    if (isSort) {
      return {
        ...(sort || {}),
        sortFun: function onSort (_sortCol, data, _oldData) {
          // 在有默认列排序的情况下，也会触发这个方法；
          const { sortFun, backSource } = sort;

          if (backSource) {
            const columnsNew = columnsInner.map(item => {
              for (let i = 0; i < _sortCol.length; i++) {
                if (item.dataIndex === _sortCol[i].field) {
                  return mode === "single"
                    ? {
                      ...item,
                      order: _sortCol[i].order
                    }
                    : {
                      ...item,
                      order: _sortCol[i].order,
                      orderNum: _sortCol[i].orderNum
                    }
                }
              }

              return omit(item, ["orderNum", "order"]);
            })
            setColumnsInner(columnsNew);
          }

          queryFiltersRef.current = {
            ...queryFiltersRef.current,
            sort: _sortCol
          }

          sortFun && sortFun(_sortCol, data, _oldData)
        }
      }
    }
    return {};
  }, [sort, isSort, columnsInner])

  // 内部受控被选择行。
  const rowSelectionConfig = useMemo(() => {
    const newRowSelection = {}
    if (rowSelection) {
      return { // selectedRowKeys单独内部维护  防止外部传入不受控 导致render时是错误的selectedRowKeys
        ...rowSelection,
        selectedRowkeys,
        onChange: (selectedRowKeys, selectedRows) => {
          console.log(selectedRowKeys, selectedRows, 'selectedRowKeys, selectedRows--------')
          setSelectedRowkeys(selectedRowKeys);
          rowSelection.onChange && rowSelection.onChange(selectedRowKeys, selectedRows);
        }
      }
    }
    return newRowSelection
  }, [rowSelection, selectedRowkeys])

  const searchContentNode = useMemo(() => {
    if (!searchContent) {
      return null;
    }
    if (typeof searchContent === "function") {
      return searchContent();
    }
    return searchContent;
  }, [searchContent])

  const TableNode = useMemo(() => {
    return <TableProComp
      {...defaultProps}
      {...restProps}
      sortEnable={!!isSort}
      sort={sortConfig}
      rowSelection={rowSelectionConfig}
      mode={innerMode}
      dragborder={!!isDragColumn}
      showRowNum={showRowNum}
      columns={columnsInner}
      data={dataSource}
      pagination={pageInfo && pageInfo.total ? { ...pageInfo } : false}
      onChange={onChange}
      bodyStyle={{ height: '100%' }}
      ref = {ref}
    />
  })

  return <div className={`as-dataTable as-dataTable-${innerMode}-${fillSpace ? 'fill' : 'no-fill'}`}>
    <div className='as-dataTable-search-area'>{searchContentNode}</div>
    <div className={ `as-dataTable-toolbar ${toolBarClassName || ""}`} >
      <div className='as-dataTable-operation-custom'>{toolBarContent}</div>
      {
        showModeSwitch
          ? <SwitchButton
            onChange={changeMode}
            defaultActiveType={defaultMode}

          />
          : null
      }
    </div>
    <TableContext.Provider value={{ mode: innerMode }}>
      {TableNode}
    </TableContext.Provider>

  </div>;
}))

DataTable.defaultProps = defaultProps;

export default DataTable
